import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import api, { authHeaders } from "@/lib/api";

function ServiceEditor({ svc, onSaved }) {
  const [form, setForm] = useState({
    title: svc.title || "", tagline: svc.tagline || "", hero_image: svc.hero_image || "",
    items: (svc.items || []).join("\n"), description: svc.description || "", intro: svc.intro || "",
    seo_title: svc.seo_title || "", seo_description: svc.seo_description || "",
    order: svc.order || 0, published: Boolean(svc.published),
  });
  const [saved, setSaved] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, items: form.items.split("\n").map((x) => x.trim()).filter(Boolean) };
    await api.put(`/admin/services/${svc.slug}`, payload, { headers: authHeaders() });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
    onSaved();
  };

  return (
    <form onSubmit={save} className="space-y-4 border border-white/10 bg-ink-900 p-6" data-testid={`admin-service-form-${svc.slug}`}>
      <div className="flex items-center justify-between">
        <h3 className="display-title text-xl text-copper">{svc.slug}</h3>
        {saved && <span className="font-mono text-[10px] uppercase tracking-wider text-wa">Salvat</span>}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="label-brox">Titlu</label>
          <input className="input-brox" value={form.title} onChange={set("title")} data-testid={`admin-service-title-${svc.slug}`} />
        </div>
        <div>
          <label className="label-brox">Tagline</label>
          <input className="input-brox" value={form.tagline} onChange={set("tagline")} data-testid={`admin-service-tagline-${svc.slug}`} />
        </div>
        <div className="sm:col-span-2">
          <label className="label-brox">Imagine hero (URL)</label>
          <input className="input-brox" value={form.hero_image} onChange={set("hero_image")} data-testid={`admin-service-image-${svc.slug}`} />
        </div>
        <div className="sm:col-span-2">
          <label className="label-brox">Introducere (afirmativ, pe site)</label>
          <textarea className="input-brox min-h-[70px] resize-none" value={form.intro} onChange={set("intro")} data-testid={`admin-service-intro-${svc.slug}`} />
        </div>
        <div className="sm:col-span-2">
          <label className="label-brox">Descriere detaliată</label>
          <textarea className="input-brox min-h-[110px] resize-none" value={form.description} onChange={set("description")} data-testid={`admin-service-description-${svc.slug}`} />
        </div>
        <div>
          <label className="label-brox">Elemente incluse (câte una pe linie)</label>
          <textarea className="input-brox min-h-[110px] resize-none" value={form.items} onChange={set("items")} data-testid={`admin-service-items-${svc.slug}`} />
        </div>
        <div className="space-y-4">
          <div>
            <label className="label-brox">SEO — titlu</label>
            <input className="input-brox" value={form.seo_title} onChange={set("seo_title")} data-testid={`admin-service-seotitle-${svc.slug}`} />
          </div>
          <div>
            <label className="label-brox">SEO — descriere</label>
            <textarea className="input-brox min-h-[70px] resize-none" value={form.seo_description} onChange={set("seo_description")} data-testid={`admin-service-seodesc-${svc.slug}`} />
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <label className="flex cursor-pointer items-center gap-2 text-sm text-paper">
          <input type="checkbox" className="h-5 w-5 accent-[#D96B27]" checked={form.published} onChange={set("published")} data-testid={`admin-service-published-${svc.slug}`} />
          Publicat
        </label>
        <button type="submit" className="btn-primary !px-6 !py-2.5 !min-h-[44px] text-base" data-testid={`admin-service-save-${svc.slug}`}>SALVEAZĂ</button>
      </div>
    </form>
  );
}

function FaqsSection() {
  const [faqs, setFaqs] = useState([]);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({ question: "", answer: "", service_slug: "", order: 0 });
  const [editingId, setEditingId] = useState(null);

  const load = () => {
    api.get("/admin/faqs", { headers: authHeaders() }).then(({ data }) => setFaqs(data || []));
    api.get("/admin/services", { headers: authHeaders() }).then(({ data }) => setServices(data || []));
  };
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (editingId) {
      await api.put(`/admin/faqs/${editingId}`, form, { headers: authHeaders() });
    } else {
      await api.post("/admin/faqs", form, { headers: authHeaders() });
    }
    setForm({ question: "", answer: "", service_slug: "", order: 0 });
    setEditingId(null);
    load();
  };

  const remove = (id) => {
    if (!window.confirm("Ștergi această întrebare?")) return;
    api.delete(`/admin/faqs/${id}`, { headers: authHeaders() }).then(load);
  };

  return (
    <div className="mt-12" data-testid="admin-faqs-section">
      <h2 className="display-title mb-6 text-2xl">Întrebări frecvente</h2>
      <form onSubmit={save} className="mb-6 space-y-4 border border-copper/40 bg-ink-900 p-6" data-testid="admin-faq-form">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label-brox">Întrebarea *</label>
            <input required className="input-brox" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="admin-faq-question-input" />
          </div>
          <div>
            <label className="label-brox">Serviciu (opțional)</label>
            <select className="input-brox" value={form.service_slug} onChange={(e) => setForm({ ...form, service_slug: e.target.value })} data-testid="admin-faq-service-select">
              <option value="">General (toate paginile)</option>
              {services.map((s) => (
                <option key={s.slug} value={s.slug}>{s.slug}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="label-brox">Răspuns *</label>
            <textarea required className="input-brox min-h-[80px] resize-none" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} data-testid="admin-faq-answer-input" />
          </div>
        </div>
        <div className="flex gap-3">
          <button type="submit" className="btn-primary !px-5 !py-2.5 !min-h-[44px] text-base" data-testid="admin-faq-save-btn">
            {editingId ? "ACTUALIZEAZĂ" : "ADAUGĂ ÎNTREBARE"}
          </button>
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm({ question: "", answer: "", service_slug: "", order: 0 }); }} className="btn-ghost !px-5 !py-2.5 !min-h-[44px] text-base">
              ANULEAZĂ
            </button>
          )}
        </div>
      </form>

      <div className="space-y-3">
        {faqs.map((f) => (
          <div key={f._id} className="flex items-start justify-between gap-4 border border-white/10 bg-ink-900 px-5 py-4" data-testid={`admin-faq-${f._id}`}>
            <div>
              <p className="font-display text-lg font-bold text-paper">{f.question}</p>
              <p className="mt-1 text-sm text-muted-foreground">{f.answer}</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-copper">{f.service_slug || "general"}</p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button onClick={() => { setEditingId(f._id); setForm({ question: f.question, answer: f.answer, service_slug: f.service_slug || "", order: f.order || 0 }); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="flex h-9 w-9 items-center justify-center border border-white/15 text-paper/70 hover:border-copper hover:text-copper" aria-label="Editează" data-testid={`admin-faq-edit-${f._id}`}>
                <Pencil className="h-4 w-4" />
              </button>
              <button onClick={() => remove(f._id)} className="flex h-9 w-9 items-center justify-center border border-white/15 text-paper/70 hover:border-red-500 hover:text-red-400" aria-label="Șterge" data-testid={`admin-faq-delete-${f._id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ContentTab() {
  const [services, setServices] = useState(null);
  const [openSlug, setOpenSlug] = useState(null);

  const load = () => api.get("/admin/services", { headers: authHeaders() }).then(({ data }) => setServices(data || []));
  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="admin-content-tab">
      <h2 className="display-title mb-6 text-2xl">Servicii</h2>
      <div className="space-y-3">
        {services === null && <p className="text-sm text-muted-foreground">Se încarcă...</p>}
        {services?.map((svc) => (
          <div key={svc.slug} className="border border-white/10 bg-ink-900">
            <button
              onClick={() => setOpenSlug(openSlug === svc.slug ? null : svc.slug)}
              className="flex w-full items-center justify-between px-5 py-4 text-left"
              data-testid={`admin-service-toggle-${svc.slug}`}
            >
              <span className="font-display text-lg font-bold uppercase tracking-wide text-paper">{svc.title}</span>
              <span className="font-mono text-[10px] uppercase tracking-wider text-copper">{openSlug === svc.slug ? "ÎNCHIDE" : "EDITEAZĂ"}</span>
            </button>
            {openSlug === svc.slug && (
              <div className="border-t border-white/10 p-5">
                <ServiceEditor svc={svc} onSaved={load} />
              </div>
            )}
          </div>
        ))}
      </div>
      <FaqsSection />
    </div>
  );
}
